import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Row } from "reactstrap";
import { AuthService } from "services/AuthService";
import { AuthData, useAuth } from "context/AuthProvider";
import emailImg from "../../assets/images/email.png";

export const CUSTOM_API_KEY_ONCE = "zuluCustomApiKeyOnce";
export const CUSTOM_API_KEY_NEXT = "zuluCustomApiKeyNext";
/** Optional merchant dashboard path after existing-customer store join. */
export const CUSTOM_API_KEY_DASHBOARD = "zuluCustomApiKeyDashboard";

type LocationState = {
  apiKey?: string;
};

/**
 * One-time reveal of the custom store API key after OTP verification.
 * Raw key is never available from the API again after this screen.
 */
const CustomApiKeyReveal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, , , , refreshUser] = useAuth() as AuthData;
  const state = (location.state || {}) as LocationState;

  const apiKey = useMemo(() => {
    const fromState = typeof state.apiKey === "string" ? state.apiKey.trim() : "";
    if (fromState) {
      return fromState;
    }
    return (sessionStorage.getItem(CUSTOM_API_KEY_ONCE) || "").trim();
  }, [state.apiKey]);

  const [copied, setCopied] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [copyError, setCopyError] = useState("");

  document.title = "API Key | Zulu Courier";

  const handleCopy = async () => {
    setCopyError("");
    if (!apiKey) {
      return;
    }
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (_error) {
      setCopyError("Could not copy automatically. Select the key and copy it manually.");
    }
  };

  const handleContinue = async () => {
    setContinuing(true);
    const next = (sessionStorage.getItem(CUSTOM_API_KEY_NEXT) || "merchant").trim();
    const dashboardPath = (
      sessionStorage.getItem(CUSTOM_API_KEY_DASHBOARD) || ""
    ).trim();
    sessionStorage.removeItem(CUSTOM_API_KEY_ONCE);
    sessionStorage.removeItem(CUSTOM_API_KEY_NEXT);
    sessionStorage.removeItem(CUSTOM_API_KEY_DASHBOARD);
    sessionStorage.removeItem("zuluMerchantOtpEmail");
    sessionStorage.removeItem("zuluMerchantPortalReturn");

    try {
      // Existing customer join: open merchant dashboard (keep customer session).
      if (next === "merchant_dashboard" && dashboardPath) {
        window.open(dashboardPath, "_blank", "noopener,noreferrer");
        navigate("/business_customer/stores", { replace: true });
        return;
      }
      if (next === "merchant") {
        AuthService.userLogout();
        await refreshUser();
        window.location.replace("/merchant");
        return;
      }
      navigate(`/${next}/place-orders`, { replace: true });
    } finally {
      setContinuing(false);
    }
  };

  if (!apiKey) {
    return (
      <div className="payment-card-wrapper">
        <div className="p-3">
          <Card className="payment-card">
            <CardBody className="px-5 py-5 text-center">
              <h4 className="text-muted mb-3">API key unavailable</h4>
              <p className="text-muted font-size-16 mb-4">
                This key is only shown once after email verification. Sign in to the
                Merchant Portal to continue. If you lost the key, ask Zulu support to
                rotate it.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: "#34C38F" }}
                onClick={() => window.location.replace("/merchant")}
              >
                Go to Merchant Portal
              </button>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-card-wrapper">
      <div className="p-3">
        <Card className="payment-card">
          <div className="card-header">
            <Row>
              <div className="image-container">
                <img src={emailImg} className="image" alt="API key" />
              </div>
            </Row>
          </div>
          <CardBody className="px-5 py-3">
            <Row className="mb-3 mt-4">
              <h4 className="text-muted text-center">Your Zulu API key</h4>
              <p className="text-muted text-center font-size-16">
                Copy this key now and store it securely. It is shown only once and is
                used by your ecommerce platform to call Zulu (rates and orders).
              </p>
              <div
                className="alert alert-warning mt-2 mb-3"
                role="status"
              >
                Do not share this key publicly. If you lose it, you will need a new key
                from Zulu.
              </div>
              <div className="form-group">
                <label className="form-label text-muted" htmlFor="zulu-api-key">
                  API key
                </label>
                <textarea
                  id="zulu-api-key"
                  className="form-control font-monospace"
                  rows={3}
                  readOnly
                  value={apiKey}
                  onFocus={(event) => event.currentTarget.select()}
                />
              </div>
              {copyError ? (
                <div className="alert alert-danger mt-2 mb-0" role="alert">
                  {copyError}
                </div>
              ) : null}
            </Row>
            <Row className="mb-3 g-2">
              <div className="d-flex justify-content-center flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleCopy}
                  disabled={continuing}
                >
                  {copied ? "Copied" : "Copy key"}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ backgroundColor: "#34C38F", borderColor: "#34C38F" }}
                  onClick={handleContinue}
                  disabled={continuing}
                >
                  {continuing ? "Please wait…" : "I saved my key — continue"}
                </button>
              </div>
            </Row>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default CustomApiKeyReveal;
